/**
 * SequencerStep Component
 *
 * Individual sortable step card for the step sequencer practice exercise.
 * Uses @dnd-kit/sortable for drag-and-drop reordering.
 *
 * Visual states:
 * - Default: white card with grip handle
 * - Dragging: elevated shadow, slight scale, reduced opacity
 * - Correct (after submit): green left border
 * - Incorrect (after submit): red left border + "Should be #N"
 *
 * Touch-friendly: drag handle is 48px minimum, smooth animations.
 * Safety-critical steps show a red shield icon.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ShieldAlert, Check, X } from 'lucide-react';
import { cn } from '../../../lib/utils';

// Evaluated once at module load — safe for SSR guard, avoids MediaQueryList allocation per render
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface SequencerStepProps {
  /** Unique step ID */
  id: string;
  /** Step description text */
  description: string;
  /** Whether this step is safety-critical */
  isSafetyCritical: boolean;
  /** 1-based display position in the current list */
  position: number;
  /** Whether results are being shown (disables dragging) */
  showResults?: boolean;
  /** Whether this step is in the correct position (only meaningful when showResults=true) */
  isCorrect?: boolean;
  /** The correct position number (only shown when incorrect and showResults=true) */
  correctPosition?: number;
}

export function SequencerStep({
  id,
  description,
  isSafetyCritical,
  position,
  showResults = false,
  isCorrect,
  correctPosition,
}: SequencerStepProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: showResults,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      role="listitem"
      style={{
        ...style,
        ...(prefersReducedMotion ? { transition: 'none' } : {}),
      }}
      className={cn(
        'flex items-center gap-3 rounded-2xl bg-white p-4 ring-1 transition-shadow duration-150',
        // Default state
        !showResults && !isDragging && 'ring-gray-200',
        // Dragging state
        isDragging && 'z-10 scale-[1.02] shadow-lg opacity-90 ring-guild-green/40',
        // Results: correct
        showResults && isCorrect && 'ring-emerald-300 border-l-4 border-emerald-500',
        // Results: incorrect
        showResults && !isCorrect && 'ring-red-300 border-l-4 border-red-500',
      )}
    >
      {/* Drag handle */}
      {!showResults && (
        <button
          type="button"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-gray-400 transition-colors duration-150 active:bg-gray-100 active:text-gray-600 touch-none"
          aria-label={`Reorder step ${position}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={20} strokeWidth={1.5} />
        </button>
      )}

      {/* Result icon (replaces drag handle in results) */}
      {showResults && (
        <div
          className={cn(
            'flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl',
            isCorrect ? 'bg-emerald-50' : 'bg-red-50',
          )}
        >
          {isCorrect ? (
            <Check size={20} strokeWidth={2} className="text-emerald-600" />
          ) : (
            <X size={20} strokeWidth={2} className="text-red-600" />
          )}
        </div>
      )}

      {/* Position number */}
      <div
        className={cn(
          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
          showResults && isCorrect && 'bg-emerald-100 text-emerald-700',
          showResults && !isCorrect && 'bg-red-100 text-red-700',
          !showResults && 'bg-gray-100 text-gray-600',
        )}
      >
        {position}
      </div>

      {/* Step content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-op-text-primary line-clamp-2">
          {description}
        </p>
        {/* Incorrect position hint */}
        {showResults && !isCorrect && correctPosition !== undefined && (
          <p className="mt-1 text-xs text-red-600">
            Should be #{correctPosition}
          </p>
        )}
      </div>

      {/* Safety-critical indicator */}
      {isSafetyCritical && (
        <div
          className="flex flex-shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-1"
          title="Safety-critical step"
        >
          <ShieldAlert size={14} strokeWidth={1.5} className="text-red-600" />
          <span className="hidden text-[10px] font-semibold text-red-600 sm:inline">
            Safety
          </span>
        </div>
      )}
    </div>
  );
}
